/**
 * Menu nativo "Favoritos" (só no app desktop).
 *
 * Componente HEADLESS — não renderiza nada. Ele é a ponte entre o hook
 * `useMyDesktops` (a fonte única) e o menu da barra do sistema:
 *
 *   store/servidor ──useMyDesktops──▶ publishFavoritesMenu ──IPC──▶ Menu nativo
 *                          ▲                                            │
 *                          └────────── switchTo() ◀──IPC── clique ──────┘
 *
 * O menu não sabe trocar de desktop: ele manda um comando, e quem troca é o
 * MESMO `switchTo` que a barra do web usa. Mudou a lógica de troca, muda num
 * lugar só.
 *
 * No web este componente não monta (Main só o renderiza quando isDesktopApp()).
 */

import { useEffect } from "react";
import { useMyDesktops } from "~/components/MyDesktopsBar/useMyDesktops";
import { isFeatureEnabled } from "~/data/Services/settings";
import {
  isDesktopApp,
  publishFavoritesMenu,
  onFavoritesCommand,
} from "./platform";

const NativeFavoritesMenu = () => {
  // Dois eixos ORTOGONAIS:
  //   - a flag `myDesktops` decide se a FEATURE existe (gate de release)
  //   - a plataforma decide qual SUPERFÍCIE a renderiza (barra vs menu nativo)
  // Ambos lidos em use time.
  const enabled = isFeatureEnabled("myDesktops") && isDesktopApp();

  const {
    sections,
    loaded,
    currentDesktopId,
    isDirty,
    switchTo,
    openAllDesktops,
  } = useMyDesktops(enabled);

  // Publica o estado do menu sempre que a lista/dirty/ativo mudar.
  // Só publica DEPOIS do primeiro fetch: o Main remonta a árvore (key themeReady)
  // e publicar a lista vazia do estado inicial faria o menu piscar
  // "Nenhum favorito" a cada remontagem.
  useEffect(() => {
    if (!enabled || !loaded) return;
    publishFavoritesMenu({
      items: sections.map((d) => ({
        id: d.id,
        label: d.name,
        dirty: isDirty(d),
        active: d.id === currentDesktopId,
      })),
    });
  }, [enabled, loaded, sections, currentDesktopId, isDirty]);

  // Recebe os cliques do menu nativo e roteia para as MESMAS ações do web.
  useEffect(() => {
    if (!enabled) return undefined;
    return onFavoritesCommand((command) => {
      if (command?.type === "switch") {
        const target = sections.find((d) => d.id === command.id);
        if (!target) {
          console.warn(
            "[NativeFavoritesMenu] desktop do menu não encontrado na lista:",
            command.id,
            sections.map((d) => d.id),
          );
          return;
        }
        switchTo(target);
        return;
      }
      if (command?.type === "openAll") {
        openAllDesktops();
      }
    });
  }, [enabled, sections, switchTo, openAllDesktops]);

  return null;
};

export default NativeFavoritesMenu;
